import * as Sentry from "@sentry/react"

import { router } from "@/router"

import { isDev, isProd } from "../env"
import type { SubComponent } from "./monitoring"

export function initSentry(subcomponent: SubComponent) {
  Sentry.init({
    dsn: "https://d1dbf16c1fa2a180263dc08469a69845@o4509807260336129.ingest.de.sentry.io/4509829731385424",
    // this will store exceptions to indexedDB - turn it off in case of problems
    // https://docs.sentry.io/platforms/javascript/configuration/transports/#offline-caching
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    enabled: isProd() || isDev() /* || isLocalDev() */,
    release: process.env.SENTRY_RELEASE ?? "not-set",
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProd() ? 1 : 0,
    integrations: [
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
        networkRequestHeaders: ["Cache-Control"],
        networkResponseHeaders: ["Referrer-Policy"],
        networkDetailAllowUrls: [
          window.location.origin,
          "s3.fr-par.scw.cloud",
          "instaclaim.ai",
          "instacover.ai",
          "api/trpc",
        ],
      }),
      Sentry.browserTracingIntegration(),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled.
    // Faro ingest (faro.svc.instacover.ai) je vyloučený přes (?!faro\.) — jinak by Sentry
    // do Faro requestu přidal sentry-trace/baggage hlavičky a faro.receiver by na 4-hlavičkový
    // preflight nevrátil CORS (Access-Control-Allow-Origin) → telemetrie by padala.
    tracePropagationTargets: ["localhost", /^https:\/\/(?!faro\.).*instacover\.ai.*/],
    tracesSampleRate: subcomponent === "admin" ? 0 : 1,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.type === "http") {
        if (breadcrumb.data?.url && breadcrumb.data?.url.length > 100) {
          breadcrumb.data.url = breadcrumb.data?.url.substr(0, 100) + "<ZKRACENO>"
        }
      }

      return breadcrumb
    },
    ignoreErrors: [
      // https://linear.app/instacover/issue/IC-311/typeerror-job-rejected-for-non-app-bound-domain
      "Job rejected for non app-bound domain",
      // useVideoConstraints errors that we cannot do anything about
      "NotAllowedError: Permission denied",
      "GetUserMediaTimeout",
      // no internet connection
      "Failed to fetch",
      "Load failed",
      "Network Error",
      /Loading chunk .* failed/,
      // user explicitly denied Service Worker permission
      "The user denied permission to use Service Worker",
      // browser extension injection (Firefox/YouTube addons)
      /__firefox__\./,
      // SDK integration: integrátor nemá postEvent metodu
      "Error invoking postEvent: Method not found",
    ],
    beforeSend(event) {
      // Service Worker registration rejection (vite-plugin-pwa registerSW.js) — INSTACAR-BM
      // "Error: Rejected" je příliš generické na ignoreErrors, filtrujeme podle zdroje
      const isSwRejection = event.exception?.values?.some(
        (e) => e.value === "Rejected" && e.stacktrace?.frames?.some((f) => f.filename?.includes("registerSW")),
      )
      if (isSwRejection) return null

      return event
    },
  })

  Sentry.getCurrentScope().setTag("component", "instacar").setTag("subcomponent", subcomponent)
}
